import React, { useEffect, useMemo } from "react";
import { Music2, Pause, Play } from "lucide-react";
import { assetUrl } from "../../lib/api";
import { buildSegments } from "../../hooks/useVideoStudio";
import { useSequencePlayer } from "../../hooks/useSequencePlayer";
import { CaptionOverlay } from "./CaptionOverlay";
import { Frame } from "./Frame";

const fmt = (x) => `${Math.floor(x / 60)}:${String(Math.floor(x % 60)).padStart(2, "0")}`;

const Controls = ({ p, segments, music }) => (
  <div className="mt-3 flex items-center gap-3">
    <button onClick={p.toggle} data-testid="preview-play-btn" aria-label={p.playing ? "Pause" : "Play"}
      className="w-9 h-9 shrink-0 rounded-full bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-400 active:scale-95 transition-[background-color,transform]">
      {p.playing ? <Pause size={15} /> : <Play size={15} className="ml-0.5" />}
    </button>
    <div className="relative flex-1 h-6 flex items-center">
      <input type="range" min={0} max={p.total || 0} step={0.05} value={Math.min(p.t, p.total)} aria-label="Preview position"
        onChange={(e) => p.seek(Number(e.target.value), p.playing)} data-testid="preview-scrubber" className="w-full accent-emerald-500" />
      {segments.slice(1).map((s) => (
        <span key={s.id} style={{ left: `${(s.offset / p.total) * 100}%` }} className="absolute top-1/2 -translate-y-1/2 w-0.5 h-3 rounded bg-slate-400/70 pointer-events-none" />
      ))}
    </div>
    <span className="font-mono text-[11px] text-slate-300 shrink-0" data-testid="preview-time">{fmt(p.t)} / {fmt(p.total)}</span>
    {music && <Music2 size={14} className="text-emerald-400 shrink-0" data-testid="preview-music-on" aria-label="Music on" />}
  </div>
);

export const PreviewPlayer = ({ video, captions, jump, onClip }) => {
  const segments = useMemo(() => buildSegments(video.clips), [video.clips]);
  const music = video.music?.status === "ready" && video.music.url ? video.music : null;
  const p = useSequencePlayer(segments, music);
  const { seek } = p;

  useEffect(() => { if (jump) seek(jump.time, jump.play); }, [jump, seek]);
  useEffect(() => { onClip?.(p.seg?.id || null); }, [p.seg?.id, onClip]);

  return (
    <div data-testid="preview-player">
      <Frame aspect={video.aspect}>
        <video ref={p.videoRef} src={assetUrl(p.seg.url)} muted playsInline preload="auto" onClick={p.toggle}
          onLoadedMetadata={p.onLoaded} onPlaying={p.onPlaying} onSeeked={p.onSeeked}
          className="w-full h-full object-contain cursor-pointer" data-testid="preview-video" />
        <CaptionOverlay captions={captions} t={p.t} />
      </Frame>
      {music && <audio ref={p.audioRef} src={assetUrl(music.url)} loop preload="auto" data-testid="preview-audio" />}
      <Controls p={p} segments={segments} music={music} />
    </div>
  );
};

export default PreviewPlayer;
